import React, { useState } from 'react';
import { createPortal } from 'react-dom';

// Viewport-aware tooltip. Renders the bubble via a portal at document.body
// so it escapes any ancestor with overflow:hidden (the modal shell) and
// clamps horizontally so it never overflows the viewport on either edge.
// The `align` prop is kept for backward compat but is no longer needed —
// positioning is automatic. Defaults to "above the icon" and flips below
// if there isn't room (near the top of the viewport).
export default function FieldTooltip({ text }: { text: string; align?: 'center' | 'right' }) {
  const [coords, setCoords] = useState<{ left: number; top: number; placeBelow: boolean } | null>(null);
  const iconRef = React.useRef<HTMLSpanElement>(null);

  const TOOLTIP_WIDTH = 280;
  const EDGE_MARGIN = 8;
  const TOOLTIP_EST_HEIGHT = 80;

  const handleEnter = () => {
    if (!iconRef.current) {return;}
    const rect = iconRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - EDGE_MARGIN));
    const placeBelow = rect.top < TOOLTIP_EST_HEIGHT + EDGE_MARGIN;
    const top = placeBelow ? rect.bottom + 6 : rect.top - 6;
    setCoords({ left, top, placeBelow });
  };

  return (
    <span
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setCoords(null)}
    >
      &#9432;
      {coords && createPortal(
        <span
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            transform: coords.placeBelow ? 'none' : 'translateY(-100%)',
            width: TOOLTIP_WIDTH,
            padding: '8px 10px',
            background: '#1f2937',
            color: '#f3f4f6',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontWeight: 'normal',
            whiteSpace: 'normal',
            textAlign: 'left',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

